import { faPrint } from '@fortawesome/pro-solid-svg-icons/faPrint';
import { faShareNodes } from '@fortawesome/pro-solid-svg-icons/faShareNodes';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ActionIcon, Button, Drawer, Group, Menu, Title } from '@mantine/core';
import { useIntl } from 'react-intl';

import { AdditionalDetailsSection } from './components/additional-details-section/AdditionalDetailsSection';
import { DocumentsSection } from './components/documents-section/DocumentsSection';
import { HandlingUnitSection } from './components/handling-unit-section/HandlingUnitSection';
import { ShipmentDetailsSection } from './components/shipment-details-section/ShipmentDetailsSection';
import { ShipmentSummarySection } from './components/shipment-summary-section/ShipmentSummarySection';
import { TrackingHistorySection } from './components/tracking-history-section/TrackingHistorySection';
import { freightDetailsMessages } from './freight-details-messages';
import { FreightStatusBadge } from './freight-status-badge/FreightStatusBadge';
import styles from './FreightDetails.module.css';
import { parseFreightStatus } from './parse-freight-status';

import type { FreightDetailsProps } from './freight-details-types';
import type { RefObject } from 'react';

/**
 * Resolves the `portalRef` prop (a React ref, a raw element, or null) to the
 * `HTMLElement | undefined` target Mantine's `portalProps.target` expects.
 * A null prop or an unset ref resolves to `undefined`, letting Mantine fall
 * back to its default body portal.
 */
const resolvePortalTarget = (portalRef: RefObject<HTMLElement> | HTMLElement | null): HTMLElement | undefined => {
  if (portalRef === null) return undefined;
  if ('current' in portalRef) return portalRef.current ?? undefined;
  return portalRef;
};

/**
 * Top-level, store-agnostic Freight Details view. Owns the right-side Mantine
 * Drawer and composes the six detail sections into a two-column grid. All data
 * arrives via props and all side effects flow out through callbacks; the host
 * supplies `portalRef`/`zIndex` for drawer placement and stacking.
 */
export const FreightDetails = ({
  isOpen,
  shipment,
  originAddress,
  displayDimensionUnit,
  displayWeightUnit,
  currencyCode,
  trackingHistory,
  portalRef,
  zIndex,
  onPrintDocument,
  onShare,
  onPrintAll,
  onTrack,
  onClose,
  onCopy,
  onVoid,
  orderNumber,
  onOrderLinkClick,
}: FreightDetailsProps) => {
  const intl = useIntl();

  const target = resolvePortalTarget(portalRef);

  // Null bolNumber → fall back to the generic localized "Freight Details" title
  // rather than rendering "BOL #" with an empty number.
  const titleText =
    shipment.bolNumber !== null
      ? intl.formatMessage(freightDetailsMessages.bolTitle, { bolNumber: shipment.bolNumber })
      : intl.formatMessage(freightDetailsMessages.title);

  return (
    <Drawer
      classNames={{ body: styles.drawerBodySlot, content: styles.drawerContent, header: styles.header, title: styles.titleSlot }}
      onClose={onClose}
      opened={isOpen}
      padding={0}
      portalProps={target ? { target } : undefined}
      position='right'
      size='100%'
      title={
        <Group className={styles.titleBar} gap='xs' justify='space-between' wrap='nowrap'>
          <Title className={styles.titleText} order={3}>
            {titleText}
          </Title>
          <Group gap='xs' wrap='nowrap'>
            {onShare && (
              <Button leftSection={<FontAwesomeIcon icon={faShareNodes} />} onClick={() => onShare?.()} variant='ghost'>
                {intl.formatMessage(freightDetailsMessages.share)}
              </Button>
            )}
            {onPrintAll && (
              <Button
                data-testid='freight-details-print-all'
                leftSection={<FontAwesomeIcon icon={faPrint} />}
                onClick={() => onPrintAll?.()}
                variant='ghost'
              >
                {intl.formatMessage(freightDetailsMessages.print)}
              </Button>
            )}
            <ActionIcon
              aria-label={intl.formatMessage(freightDetailsMessages.closeAriaLabel)}
              color='gray'
              onClick={onClose}
              variant='ghost'
            >
              <FontAwesomeIcon icon={faXmark} />
            </ActionIcon>
          </Group>
        </Group>
      }
      withCloseButton={false}
      withinPortal={target !== undefined}
      zIndex={zIndex}
    >
      <div className={styles.statusHeader}>
        <FreightStatusBadge status={parseFreightStatus(shipment.status)} />
        {orderNumber && onOrderLinkClick && (
          <Button onClick={onOrderLinkClick} variant='ghost'>
            {intl.formatMessage(freightDetailsMessages.orderLink, { orderNumber })}
          </Button>
        )}
        <Menu position='bottom-end'>
          <Menu.Target>
            <Button variant='tertiary'>{intl.formatMessage(freightDetailsMessages.shipmentActions)}</Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item disabled={!onVoid} onClick={onVoid}>
              {intl.formatMessage(freightDetailsMessages.voidMenuItem)}
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </div>

      <div className={styles.bodyGrid}>
        <div className={styles.column}>
          <ShipmentDetailsSection onCopy={onCopy} originAddress={originAddress} shipment={shipment} />
          <HandlingUnitSection displayDimensionUnit={displayDimensionUnit} displayWeightUnit={displayWeightUnit} shipment={shipment} />
          <AdditionalDetailsSection currencyCode={currencyCode} shipment={shipment} />
        </div>
        <div className={styles.column}>
          <TrackingHistorySection onTrack={onTrack} shipment={shipment} trackingHistory={trackingHistory} />
          <DocumentsSection documents={shipment.documents} onPrintDocument={onPrintDocument} />
          <ShipmentSummarySection currencyCode={currencyCode} shipment={shipment} />
        </div>
      </div>
    </Drawer>
  );
};
